import React, { useState } from "react";
import { useSelector } from "react-redux";
import CheckboxFilterItem from "web/react/components/checkbox-filter-item/checkbox-filter-item";
import { FilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { PendingFilters } from "web/react/components/feeds-filters/pending-filters";
import {
    extractFilterStateCount,
    useFilterContext,
} from "web/react/components/filters-emo/filter-container-emo";
import { FilterContentContainer } from "web/react/components/filters-emo/filter-content-container/filter-content-container";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";

import { ClearFilterButton } from "web/react/components/filters-emo/clear-filter-button/clear-filter-button";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { SortOptionSerializer } from "web/types/serializers";
import { UniversalFilterOption, UniversalFilterSection } from "web/types/universal-filters";
import * as styles from "./more-filters.css";

interface MoreFiltersProps
    extends Pick<FilterManager, "lazyFacetFetch" | "lazyFacetIsLoading" | "clearSelectedFilters"> {
    items: UniversalFilterSection[];
    sortOptions?: SortOptionSerializer[];
}

interface MoreFiltersChildrenProps
    extends Pick<FilterManager, "lazyFacetFetch" | "lazyFacetIsLoading" | "clearSelectedFilters"> {
    items: UniversalFilterSection[];
    onClick: (any) => void;
    pendingFilterInfo: PendingFilters;
    sortOptions?: SortOptionSerializer[];
    onClearAllClick?: () => void;
}

interface FilterListProps extends MoreFiltersChildrenProps {
    filterSectionList: UniversalFilterSection[];
    onClearClick: (sectionToClear?: string) => void;
    updateFromServer: () => void;
}

export function CheckboxFilters({
    items,
    onClick,
    ...props
}: MoreFiltersChildrenProps): JSX.Element {
    return (
        <div>
            {items.map((item) => {
                const hasOptions = item.option_groups[0].options.length > 0;
                return (
                    hasOptions && (
                        <View key={item.id} marginBottom={"sm"}>
                            <Heading textStyle={"subhead"}>{item.title}</Heading>
                            <ol>
                                {item.option_groups[0].options.map((item, i) => {
                                    return (
                                        <li key={i}>
                                            <CheckboxFilterItem
                                                {...{
                                                    item,
                                                    onClick,
                                                    ...props,
                                                }}
                                            />
                                        </li>
                                    );
                                })}
                            </ol>
                        </View>
                    )
                );
            })}
        </div>
    );
}

const getSelectedFiltersLabel = (
    options: UniversalFilterOption[],
    flatFilters: any[] = []
): string => {
    const checked = options.map(({ checked, label }) => {
        if (!!checked) {
            return label;
        }
    });
    const checkedOption = options.find((option) => {
        if (!!option.checked) {
            return option;
        }
    });
    options.length > 0 && flatFilters.push(checked.filter((n) => n).join(", "));
    if (checkedOption && "children" in checkedOption && !!checkedOption?.children?.length) {
        getSelectedFiltersLabel((checkedOption as any)?.children, flatFilters);
    }
    return flatFilters.filter((n) => n).join(", ");
};

function FilterList({
    items,
    filterSectionList,
    onClearClick,
    onClearAllClick,
    ...props
}: FilterListProps): JSX.Element {
    const [filterSection, setFilterSection] = useState<UniversalFilterSection | null>(null);
    const filterCountHolder = useSelector(extractFilterStateCount);
    const filterCount = !!filterSection?.id
        ? filterCountHolder[filterSection.id].breadcrumbs.length
        : null;
    const allFiltersCount = Object.values(filterCountHolder).reduce(
        (acc, { breadcrumbs }) => acc + breadcrumbs.length,
        0
    );
    const { isMobileViewport } = useDomViewport();
    const size = isMobileViewport ? "sm" : "md";

    return (
        <>
            {filterSection?.id && (
                <span className={styles.chevron}>
                    <button
                        data-testid="return-more-filters"
                        onClick={() => {
                            setFilterSection(null);
                            if (props.pendingFilterInfo.hasPending()) {
                                props.updateFromServer();
                            }
                        }}
                        className={styles.chevronButton[size]}
                        type="button"
                    >
                        <SVGIcon name="chevron-thin" rotate={90} />
                    </button>
                </span>
            )}
            <div className={styles.wrapper["sm"]}>
                {filterSection?.id ? (
                    <VStack spacing="md">
                        <ClearFilterButton
                            className={styles.clearButton}
                            active={!!filterCount}
                            onClick={() => {
                                filterSection && onClearClick(filterSection.id);
                            }}
                        />
                        {getFilterContentContainer(filterSection?.id, props)}
                    </VStack>
                ) : (
                    <div className={styles.scrollContainer}>
                        <VStack spacing="md">
                            <button onClick={onClearAllClick} disabled={allFiltersCount <= 0}>
                                <Text
                                    textStyle="body-s"
                                    className={
                                        styles.clearAllButton[
                                            allFiltersCount > 0 ? "active" : "inactive"
                                        ]
                                    }
                                >
                                    {
                                        // Clear All
                                        gettext("feed.filters.mobile_clear_all_btn_text")
                                    }
                                </Text>
                            </button>
                            {items.map((item) => {
                                const selectedSection = filterSectionList.find(
                                    (section) => section.id === item.id
                                );
                                if (selectedSection) {
                                    const checkedChildren = item.option_groups
                                        .map((group) => {
                                            const selectedFilterLabel = getSelectedFiltersLabel(
                                                group.options
                                            );
                                            return group.id === "option-group-final-price-from"
                                                ? selectedFilterLabel + " - "
                                                : selectedFilterLabel;
                                        })
                                        .filter((n) => n);
                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => {
                                                analytics.event(
                                                    "more-filters-section",
                                                    "click",
                                                    selectedSection.title
                                                );
                                                setFilterSection(selectedSection || null);
                                            }}
                                            className={styles.moreFilterRow}
                                        >
                                            <HStack justify="space-between">
                                                <div className={styles.textWrapper}>
                                                    <Text textStyle="body-s">
                                                        {selectedSection
                                                            ? selectedSection.title
                                                            : item.title}
                                                    </Text>
                                                    {checkedChildren.length > 0 && (
                                                        <Text
                                                            className={styles.selectedFiltersText}
                                                            textStyle="body-s"
                                                        >
                                                            {checkedChildren}
                                                        </Text>
                                                    )}
                                                </div>
                                                <SVGIcon
                                                    className={styles.itemChevron}
                                                    name="chevron"
                                                />
                                            </HStack>
                                        </div>
                                    );
                                }
                            })}
                        </VStack>
                    </div>
                )}
            </div>
        </>
    );
}

function getFilterContentContainer(filterId, props): JSX.Element {
    return <FilterContentContainer activeFilterId={filterId} {...props} />;
}

export function MoreFilters({ items, ...props }: MoreFiltersProps): JSX.Element {
    const {
        displayedFilters,
        onFilterItemClick,
        pendingFilterInfo,
        onClearClick,
        updateFromServer,
    } = useFilterContext();

    return (
        <FilterList
            items={items}
            onClick={onFilterItemClick}
            pendingFilterInfo={pendingFilterInfo}
            filterSectionList={displayedFilters.filter_section_list}
            onClearClick={onClearClick}
            updateFromServer={updateFromServer}
            {...props}
        />
    );
}
